import { AuthedMultiProductResponse, MultiProductResponse, ProductResponse } from "@/@types/data/collections";

export const generateProductId = (gameId: string, productType: string, productId: string): string => {
  return `${gameId}-${productType}-${productId}`;
};

/**
 * Builds on top of the revised response to avoid having to do complex data manipulation later during component rendering
 *
 * @param response
 * @returns
 */
export const processResponseWithSections = (
  response?: MultiProductResponse,
  authedResponse?: AuthedMultiProductResponse,
) => {
  // While loading this response can be undefined, in those cases do nothing.
  if (!response?.sections) {
    return response;
  }

  // Create mappings of identities to products.
  const productByProductId = new Map<string, ProductResponse>();
  response?.products.forEach((product) => {
    const productId = generateProductId(product.game, product.type, product.id);
    productByProductId.set(productId, product);
  });
  const authedProductByProductId = new Map<string, ProductResponse>();
  authedResponse?.products.forEach((product) => {
    const productId = generateProductId(product.game, product.type, product.id);
    authedProductByProductId.set(productId, product);
  });

  // Combine the maps, using the value of the authed map over the default.
  const allProductsByProductId = new Map([...productByProductId, ...authedProductByProductId]);

  // Iterate over the sections, and lookup from the auth'd map, if it exists, if not the standard mapping.
  const sectionKeys = Object.keys(response.sections);
  const newProducts: ProductResponse[] = sectionKeys.flatMap((sectionName) =>
    response.sections[sectionName]
      .filter((identity) =>
        allProductsByProductId.has(generateProductId(identity.gameId, identity.productType, identity.productId)),
      )
      .map((identity) => {
        const productId = generateProductId(identity.gameId, identity.productType, identity.productId);
        // Since we did a filter, there is no way this is undefined.
        const newProduct = allProductsByProductId.get(productId) as ProductResponse;
        return {
          ...newProduct,
          section: sectionName,
        };
      }),
  );

  // Finally return the responses with the additional product list.
  return {
    ...response,
    ...authedResponse,
    bannerUrl: response.bannerUrl,
    products: newProducts,
  };
};

export const convertProductType = (productType: string): string => {
  // If product type starts with `VAL` or `LOL` or `WR` and has a `_` after, remove it from the string
  return productType.replace(/^(VAL|LOL|WR)_(.+)$/, "$2");
};
